import React, { useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import "./TooltipProvider.scss";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

interface TooltipState {
    text: string;
    placement: TooltipPlacement;
}

const TOOLTIP_ATTRIBUTE_MAP: Array<{ attribute: string; placement: TooltipPlacement }> = [
    { attribute: "data-tooltip-left", placement: "left" },
    { attribute: "data-tooltip-right", placement: "right" },
    { attribute: "data-tooltip-bottom", placement: "bottom" },
    { attribute: "data-tooltip-top", placement: "top" },
    { attribute: "data-tooltip", placement: "top" },
];

interface TooltipProviderProps {
    children: React.ReactNode;
    delay?: number;
}

const useDebounce = (callback: Function, delay: number) => {
    const timeoutRef: React.MutableRefObject<NodeJS.Timeout | null> = useRef<NodeJS.Timeout>(null);

    return useCallback(
        (...args: any[]) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay]
    );
};

export const TooltipProvider: React.FC<TooltipProviderProps> = ({ 
    children, 
    delay = 150 
}) => {
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const currentTargetRef = useRef<HTMLElement | null>(null);

    const getTooltipData = (element: HTMLElement): TooltipState | null => {
        for (const { attribute, placement } of TOOLTIP_ATTRIBUTE_MAP) {
            const text = element.getAttribute(attribute);

            if (text) {
                return { text, placement };
            }
        }

        return null;
    };

    const showTooltip = useDebounce((element: HTMLElement, tooltipData: TooltipState) => {
        const rect = element.getBoundingClientRect();
        const gap = 12;

        const nextPosition = {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
        };

        switch (tooltipData.placement) {
            case "bottom":
                nextPosition.top += rect.height + gap;
                nextPosition.left += rect.width / 2;
                break;
            case "left":
                nextPosition.top += rect.height / 2;
                nextPosition.left -= gap;
                break;
            case "right":
                nextPosition.top += rect.height / 2;
                nextPosition.left += rect.width + gap;
                break;
            case "top":
            default:
                nextPosition.top -= gap;
                nextPosition.left += rect.width / 2;
                break;
        }

        setPosition(nextPosition);
        setTooltip(tooltipData);
    }, delay);

    const hideTooltip = useDebounce(() => {
        setTooltip(null);
    }, delay);

    const handleMouseOver = useCallback((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const relatedTarget = event.relatedTarget as HTMLElement;
        
        // Check if we're moving between child elements of the same tooltip target
        if (currentTargetRef.current?.contains(relatedTarget)) {
            return;
        }

        const tooltipElement = target.closest('[data-tooltip], [data-tooltip-top], [data-tooltip-bottom], [data-tooltip-left], [data-tooltip-right]') as HTMLElement;
        if (!tooltipElement) {
            hideTooltip();
            currentTargetRef.current = null;
            return;
        }

        const tooltipData = getTooltipData(tooltipElement);
        if (tooltipData) {
            currentTargetRef.current = tooltipElement;
            showTooltip(tooltipElement, tooltipData);
        }
    }, [showTooltip, hideTooltip]);

    const handleMouseOut = useCallback((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const relatedTarget = event.relatedTarget as HTMLElement;
        
        // Don't hide if moving to a child element
        if (target.contains(relatedTarget)) {
            return;
        }

        // Check if the related target is still within the current tooltip target
        if (currentTargetRef.current?.contains(relatedTarget)) {
            return;
        }

        hideTooltip();
        currentTargetRef.current = null;
    }, [hideTooltip]);

    React.useLayoutEffect(() => {
        document.addEventListener('mouseover', handleMouseOver);
        document.addEventListener('mouseout', handleMouseOut);

        return () => {
            document.removeEventListener('mouseover', handleMouseOver);
            document.removeEventListener('mouseout', handleMouseOut);
        };
    }, [handleMouseOver, handleMouseOut]);

    return (
        <>
            {children}
            {tooltip && createPortal(
                <div
                    className={`tooltip tooltip--${tooltip.placement}`}
                    style={{
                        top: position.top,
                        left: position.left,
                    }}
                    role="tooltip"
                >
                    <div className="tooltip_content">{tooltip.text}</div>
                    <div className="tooltip_arrow" />
                </div>,
                document.body
            )}
        </>
    );
};
